import { Input } from '@app/components/various/Fields/Input';
import { BREAKPOINTS } from '@app/constants/breakpoints';
import { DATE_STRING_FORMAT } from '@app/constants/dates';
import { cn } from '@app/utils/cn';
import { getIsValidDateString } from '@app/utils/dates';
import { useIsScreenSize } from '@app/utils/hooks';
import { isNull } from '@app/utils/is';
import { Key } from '@app/utils/keys';
import cx from 'classnames';
import dayjs from 'dayjs';
import { isEmpty } from 'ramda';
import React from 'react';

import { useDatePickerNavigationContext, useDatePickerSelectionContext, useDatePickerSettingsContext } from '../context';

interface Props {
  dataTestId?: string;
  placeholder: string;
  withinDrawer?: boolean;
  onVisibilityChange: (value: boolean) => void;
}

export const DatePickerInputs = ({ dataTestId, placeholder, withinDrawer = false, onVisibilityChange }: Props) => {
  const { startDate, endDate, isRangeSelection, setEndDate, setStartDate } = useDatePickerSelectionContext();
  const { shouldDisable } = useDatePickerSettingsContext();
  const { visibleDateCellRef } = useDatePickerNavigationContext();
  const isMobile = useIsScreenSize(BREAKPOINTS.SM);

  const [startDateString, setStartDateString] = React.useState('');
  const [endDateString, setEndDateString] = React.useState('');

  const startDateRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    !isMobile && startDateRef.current?.focus();
  }, [isMobile]);

  const updateStartDateString = React.useCallback(
    () => setStartDateString(startDate?.format(DATE_STRING_FORMAT).replace(/ /g, '') ?? ''),
    [startDate],
  );
  const updateEndDateString = React.useCallback(() => {
    setEndDateString(endDate?.format(DATE_STRING_FORMAT) ?? '');
  }, [endDate]);

  React.useEffect(updateStartDateString, [updateStartDateString]);
  React.useEffect(updateEndDateString, [updateEndDateString]);

  const handleStartDateChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const dateString = event.target.value;

      setStartDateString(dateString);

      if (isEmpty(dateString) && isNull(endDate)) {
        setStartDate(null);

        return;
      }

      const date = dayjs(dateString, DATE_STRING_FORMAT);

      if (getIsValidDateString(dateString, DATE_STRING_FORMAT) && !shouldDisable(date)) {
        setStartDate(date);
      }
    },
    [endDate, setStartDate, shouldDisable],
  );

  const handleEndDateChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const dateString = event.target.value;

      setEndDateString(dateString);

      if (isEmpty(dateString)) {
        setEndDate(null);

        return;
      }

      const date = dayjs(dateString, DATE_STRING_FORMAT);

      if (getIsValidDateString(dateString, DATE_STRING_FORMAT) && !shouldDisable(date) && !date.isBefore(startDate)) {
        setEndDate(date);
      }
    },
    [setEndDate, shouldDisable, startDate],
  );

  const handleStartInputKeyDown = (event: React.KeyboardEvent) => {
    const isShiftPressed = Key.TAB === event.key;
    const isShiftTabPressed = event.shiftKey && isShiftPressed;

    if (isNull(startDate)) {
      if (isShiftTabPressed) {
        onVisibilityChange(false);

        return;
      }

      if (isShiftPressed) {
        event.preventDefault();
        visibleDateCellRef.current?.focus();
      }

      return;
    }

    if (!isRangeSelection && isShiftPressed) {
      event.preventDefault();
      visibleDateCellRef.current?.focus();
    }

    if (isRangeSelection && isShiftTabPressed) {
      onVisibilityChange(false);
    }
  };

  const handleEndInputKeyDown = (event: React.KeyboardEvent) => {
    if (!event.shiftKey && Key.TAB === event.key) {
      event.preventDefault();
      visibleDateCellRef.current?.focus();
    }
  };

  return (
    <div
      className={cn(
        'relative flex h-10 items-center rounded-regular border border-neutral-90 px-3 has-[:focus-within]:focus-input-outline',
        {
          '-mx-2 border-none px-2': withinDrawer,
        },
      )}
    >
      <div className={cx({ 'w-full': isNull(startDate) || !isRangeSelection })}>
        <Input
          ref={startDateRef}
          data-testid={dataTestId}
          className={cn('w-full border-none px-0 focus-within:!outline-none', {
            'w-20': !isNull(startDate) && isRangeSelection,
          })}
          inputClassName={cn({
            'tabular-nums !-tracking-[0.06rem]': !isNull(startDate),
          })}
          placeholder={placeholder}
          value={startDateString}
          onChange={handleStartDateChange}
          onBlur={updateStartDateString}
          onKeyDown={handleStartInputKeyDown}
        />
      </div>
      {isRangeSelection && !isNull(startDate) && <span>{' / '}</span>}
      {isRangeSelection && !isNull(startDate) && (
        <div className="w-full">
          <Input
            className="w-full border-none px-0 focus-within:!outline-none"
            inputClassName="tabular-nums !-tracking-[0.06rem]"
            value={endDateString}
            placeholder={placeholder}
            onChange={handleEndDateChange}
            onBlur={updateEndDateString}
            onKeyDown={handleEndInputKeyDown}
          />
        </div>
      )}
    </div>
  );
};
